import { useState } from 'react';
import { useI18n, type Translations } from '../../i18n';
import { useLabels } from '../../i18n/labels';
import GenderBadge from '../shared/GenderBadge';
import { DotIcon } from '../shared/Icons';
import { personName, cornerName, nameMatches, textMatches } from '../../utils/personName';
import { weightText } from '../../utils/weight';
import { compareCategories } from '../../utils/tournament';
import type { Bout, Registration } from '../../types';
import { BADGE_XS, BADGE_XXS } from '../shared/badgeSize';

// #664 — one corner of the bout form (the red one and the blue one were two
// copies of ~38 lines). The parent keeps the rules between the corners: the
// gender filter of the other corner, the clear of the other corner on a
// gender mismatch, the derive of the age group and the weight class.

export type Corner = 'red' | 'blue';
export type CornerMode = 'athlete' | 'bout';

/** The full class strings of a corner. Tailwind reads the source text, so a
 *  class built from the corner name (`bg-${corner}-50`) would not exist. */
const CORNER_CLS: Record<Corner, { box: string; title: string; dot: string; hover: string; on: string; off: string }> = {
  red: {
    box: 'bg-red-50', title: 'text-red-700', dot: 'text-red-500', hover: 'hover:bg-red-50',
    on: 'bg-red-600 text-white', off: 'bg-white text-red-600 border border-red-300',
  },
  blue: {
    box: 'bg-blue-50', title: 'text-blue-700', dot: 'text-blue-500', hover: 'hover:bg-blue-50',
    on: 'bg-blue-600 text-white', off: 'bg-white text-blue-600 border border-blue-300',
  },
};

/** One row of the athlete list: a registration, in the form that the picker reads. */
export interface CornerAthlete {
  key: string;
  label: string;
  first_name: string;
  last_name: string;
  age_group: string;
  weight_class: string;
  gender: string;
  team_name: string;
  experience_bouts: number;
  current_weight_kg: Registration['current_weight_kg'];
  age: Registration['age'];
}

/** The state of one corner. The parent reads `athleteId` and `sourceBout`. */
export interface CornerState {
  mode: CornerMode;
  athleteId: string;
  sourceBout: string;
  search: string;
  listOpen: boolean;
  setMode: (m: CornerMode) => void;
  setAthleteId: (id: string) => void;
  setSourceBout: (id: string) => void;
  setSearch: (s: string) => void;
  setListOpen: (open: boolean) => void;
}

/** The state of a corner. An edit starts from the bout (`bout` is undefined in create mode). */
export function useCornerState(bout: Bout | undefined, corner: Corner): CornerState {
  const athlete = (corner === 'red' ? bout?.red_athlete_record_id : bout?.blue_athlete_record_id) || '';
  const source = (corner === 'red' ? bout?.red_source_bout_id : bout?.blue_source_bout_id) || '';
  const [mode, setMode] = useState<CornerMode>(source ? 'bout' : 'athlete');
  const [athleteId, setAthleteId] = useState(athlete);
  const [sourceBout, setSourceBout] = useState(source);
  const [search, setSearch] = useState('');
  const [listOpen, setListOpen] = useState(false);
  return { mode, athleteId, sourceBout, search, listOpen, setMode, setAthleteId, setSourceBout, setSearch, setListOpen };
}

/** The text of a bout in the "Winner of Bout" select: "#3 Red vs Blue | V | Elite | -71".
 *  The gender is the initial of the language (`genderShort()`): the first letter of
 *  "Male" is "M", and "M" means a woman in Lithuanian (bug 5). */
export function boutSourceLabel(b: Bout, t: Translations, genderShort: (g: string) => string): string {
  const red = cornerName(b, 'red') || t.common.tbd;
  const blue = cornerName(b, 'blue') || t.common.tbd;
  const gender = genderShort(b.red_gender || b.blue_gender || '') || '?';
  const ageGroup = b.bout_age_group || b.red_age_group || b.blue_age_group || '';
  const weightClass = b.bout_weight_class || b.red_weight_class || b.blue_weight_class || '';
  return `#${b.bout_number} ${red} ${t.common.vs} ${blue} | ${gender} | ${ageGroup} | ${weightClass}`;
}

/** The athlete list, grouped by gender, age group and weight class. */
function AthleteList({ options, search, corner, onSelect }: {
  options: CornerAthlete[]; search: string; corner: Corner; onSelect: (key: string) => void;
}) {
  const { t } = useI18n();
  // The name in either order (nameMatches), or any part of the label: the
  // category, the team, the gender.
  const filtered = options.filter(a => !search || nameMatches(search, a.first_name, a.last_name)
    || textMatches(search, a.label));
  if (!filtered.length) return <div className="px-3 py-2 text-xs text-gray-400">{t.boutForm.noMatches}</div>;

  // Group by gender → age_group → weight_class
  const groups: Record<string, CornerAthlete[]> = {};
  for (const a of filtered) {
    const key = `${a.gender}|${a.age_group}|${a.weight_class}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(a);
  }

  // The one category order (utils/tournament.ts). Before #344 this list
  // rendered in insertion order, which was right only while `registrations`
  // arrived sorted from the API.
  const ordered = Object.entries(groups).sort(([ka], [kb]) => {
    const [ga, aa, wa] = ka.split('|');
    const [gb, ab, wb] = kb.split('|');
    return compareCategories({ gender: ga, ageGroup: aa, weightClass: wa }, { gender: gb, ageGroup: ab, weightClass: wb });
  });
  return <>{ordered.map(([groupKey, athletes]) => {
    const [gender, ag, wc] = groupKey.split('|');
    return (
      <div key={groupKey}>
        <div className="sticky top-0 px-3 py-1.5 bg-gray-50 border-b border-gray-200 flex items-center gap-1.5">
          <GenderBadge gender={gender || ''} short size="xs" className="px-1" />
          <span className={`badge badge-amber ${BADGE_XS} px-1`}>{ag}</span>
          <span className={`badge badge-gray ${BADGE_XS} px-1`}>{wc}</span>
          <span className="text-[9px] text-gray-400 ml-auto">{athletes.length}</span>
        </div>
        {athletes.map(a => (
          <button key={a.key} type="button" onClick={() => onSelect(a.key)}
            className={`w-full text-left px-3 py-2 min-h-9 ${CORNER_CLS[corner].hover} border-b border-gray-50 last:border-0`}>
            {/* #552 — the row wraps on a phone: the name is the one field
                the manager needs, and the badges cut it. */}
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <span className="text-xs font-medium min-w-0 break-words">{personName(a.first_name, a.last_name)}</span>
              {a.team_name && <span className={`badge badge-gray ${BADGE_XXS} px-1.5 shrink-0`} title={t.common.team}>{a.team_name}</span>}
              {/* Stat pills right-aligned; experience always rendered so the kg
                  column ends at the same right edge across rows, making it
                  scannable down the list even when experience is 0. */}
              <span className="sm:ml-auto flex items-center gap-1.5 shrink-0">
                <span className={`badge badge-amber ${BADGE_XXS} px-1.5`} title={t.boutForm.experienceTip}>{t.boutForm.boutsBadge.replace('{n}', String(a.experience_bouts ?? 0))}</span>
                {a.age && <span className={`badge badge-blue ${BADGE_XXS} px-1.5`} title={t.common.age}>{t.boutForm.ageBadge.replace('{n}', String(a.age))}</span>}
                {weightText(a.current_weight_kg) && <span className={`badge badge-indigo ${BADGE_XXS} px-1.5`} title={t.common.weight}>{weightText(a.current_weight_kg)}</span>}
              </span>
            </div>
          </button>
        ))}
      </div>
    );
  })}</>;
}

interface CornerPickerProps {
  corner: Corner;
  state: CornerState;
  /** The athletes of the list. The parent has left out the athlete of the other corner, and the other gender. */
  options: CornerAthlete[];
  /** The bouts of the "Winner of Bout" select. The parent has filtered them by the gender of the other corner. */
  boutOptions: Bout[];
  /** The registration of the chosen athlete. The header shows its age group and its weight class. */
  reg?: CornerAthlete;
  /** The gender of the corner: from the athlete or from the source bout. */
  gender: string;
  /** A tap on an athlete of the list. The parent clears the other corner on a gender mismatch, and the class. */
  onAthlete: (key: string) => void;
  /** The class (age group and weight class) is derived again after a change of the mode or of the source bout. */
  onClassReset: () => void;
}

export default function CornerPicker({ corner, state, options, boutOptions, reg, gender, onAthlete, onClassReset }: CornerPickerProps) {
  const { t, genderShort } = useLabels();
  const cls = CORNER_CLS[corner];
  const selected = options.find(a => a.key === state.athleteId);
  const shown = state.search || (state.athleteId ? personName(selected?.first_name, selected?.last_name) : '');

  return (
    <div className={`p-3 rounded-lg ${cls.box} space-y-2`} data-testid={`corner-${corner}`}>
      <div className={`flex items-center gap-1.5 text-sm font-bold ${cls.title} flex-wrap`}>
        <DotIcon className={`w-6 h-6 ${cls.dot}`} /> {corner === 'red' ? t.boutForm.redCorner : t.boutForm.blueCorner}
        {gender && <GenderBadge gender={gender} size="xs" className="font-normal" />}
        {reg?.age_group && <span className="text-[10px] font-normal px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{reg.age_group}</span>}
        {reg?.weight_class && <span className="text-[10px] font-normal px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{reg.weight_class}</span>}
      </div>
      <div className="flex gap-1 mb-1">
        <button type="button" onClick={() => { state.setMode('athlete'); state.setSourceBout(''); onClassReset(); }}
          className={`text-xs px-3 py-2 min-h-9 rounded-full font-semibold ${state.mode === 'athlete' ? cls.on : cls.off}`}>{t.boutForm.athlete}</button>
        <button type="button" onClick={() => { state.setMode('bout'); state.setAthleteId(''); state.setSearch(''); onClassReset(); }}
          className={`text-xs px-3 py-2 min-h-9 rounded-full font-semibold ${state.mode === 'bout' ? cls.on : cls.off}`}>{t.boutForm.winnerOfBout}</button>
      </div>
      {state.mode === 'athlete' ? (
        // The list closes when the focus leaves the field AND the list (a Tab
        // into an option keeps it). No timer: a timer that a blur started at
        // the open of the modal closed a list that the user opened a moment
        // later (the red picker of Edit bout, #664). A press on the list keeps
        // the focus in the field, so a tap on an option needs no delay.
        <div className="relative"
          onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) state.setListOpen(false); }}>
          {/* #552 — the list opens on a click, an arrow key or typing, not on
              focus: the first focus of Edit bout is this field, and the list
              covered the form when the modal opened. */}
          <input className="input" placeholder={t.boutForm.searchAthlete} value={shown} data-testid={`corner-${corner}-search`}
            onChange={e => { state.setSearch(e.target.value); state.setListOpen(true); if (!e.target.value) state.setAthleteId(''); }}
            onClick={() => { state.setListOpen(true); if (state.athleteId) state.setSearch(''); }}
            onKeyDown={e => { if (e.key === 'ArrowDown') state.setListOpen(true); }} />
          {state.listOpen && (
            <div className="absolute z-10 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-[50vh] overflow-y-auto"
              data-testid={`corner-${corner}-list`} onMouseDown={e => e.preventDefault()}>
              <AthleteList options={options} search={state.search} corner={corner}
                onSelect={key => { onAthlete(key); state.setSearch(''); state.setListOpen(false); }} />
            </div>
          )}
        </div>
      ) : (
        <select className="input" value={state.sourceBout} data-testid={`corner-${corner}-bout`} onChange={e => { state.setSourceBout(e.target.value); onClassReset(); }}>
          <option value="">{t.boutForm.selectBout}</option>
          {[...boutOptions].sort((a, b) => a.bout_number - b.bout_number).map(b => (
            <option key={b.id} value={b.id}>{boutSourceLabel(b, t, genderShort)}</option>
          ))}
        </select>
      )}
    </div>
  );
}
